// Декодирует аудио по URL через Web Audio API и возвращает массив RMS-пиков
// длиной `bars`, нормализованный в [0..1]. Используется в FullPlayer /
// RecapPlayerBar / AudioMiniPlayer для waveform (вместо синтетического sin).
//
// Кэш — in-memory Map по pathname URL'а: presigned-URL'ы содержат TTL-сигнатуру
// и query-параметры, которые меняются между сессиями; pathname стабилен.
//
// inFlight — дедуп параллельных mount'ов (mini-player + RecapPlayerBar) до
// заполнения peaksCache: один fetch/decode на ключ, без abort общей загрузки
// при размонтировании одного потребителя.

import { useEffect, useRef, useState } from "react";
import { isAbortOrTimeoutError } from "@/lib/fetch-timeout";

const peaksCache = new Map<string, number[]>();
const inFlight = new Map<string, Promise<number[] | null>>();

function cacheKey(url: string, bars: number): string {
	try {
		const u = new URL(url);
		return `${u.pathname}|${bars}`;
	} catch {
		return `${url}|${bars}`;
	}
}

interface WebkitWindow {
	webkitAudioContext?: typeof AudioContext;
}

function getAudioContextCtor(): typeof AudioContext | null {
	if (typeof window === "undefined") return null;
	return window.AudioContext ?? (window as WebkitWindow).webkitAudioContext ?? null;
}

async function decodePeaks(url: string, bars: number): Promise<number[] | null> {
	const resp = await fetch(url);
	if (!resp.ok) return null;
	const arrayBuffer = await resp.arrayBuffer();

	const Ctor = getAudioContextCtor();
	if (!Ctor) return null;
	const ctx = new Ctor();
	try {
		const buf = await ctx.decodeAudioData(arrayBuffer.slice(0));

		const channels = buf.numberOfChannels;
		const len = buf.length;
		const blockSize = Math.max(1, Math.floor(len / bars));
		const out: number[] = new Array(bars).fill(0);

		const channelData: Float32Array[] = [];
		for (let c = 0; c < channels; c++) channelData.push(buf.getChannelData(c));

		let globalMax = 0;
		for (let i = 0; i < bars; i++) {
			const start = i * blockSize;
			const end = Math.min(start + blockSize, len);
			let sumSq = 0;
			let count = 0;
			for (let j = start; j < end; j++) {
				for (let c = 0; c < channels; c++) {
					const v = channelData[c]?.[j] ?? 0;
					sumSq += v * v;
					count++;
				}
			}
			const rms = count > 0 ? Math.sqrt(sumSq / count) : 0;
			out[i] = rms;
			if (rms > globalMax) globalMax = rms;
		}

		return globalMax > 0 ? out.map((v) => Math.min(1, v / globalMax)) : out;
	} finally {
		void ctx.close();
	}
}

function loadPeaks(url: string, bars: number): Promise<number[] | null> {
	const key = cacheKey(url, bars);
	const cached = peaksCache.get(key);
	if (cached) return Promise.resolve(cached);

	const existing = inFlight.get(key);
	if (existing) return existing;

	const task = decodePeaks(url, bars)
		.then((normalized) => {
			if (normalized) peaksCache.set(key, normalized);
			return normalized;
		})
		.catch((err: unknown) => {
			if (isAbortOrTimeoutError(err)) return null;
			// eslint-disable-next-line no-console -- допустимо для аудио ошибок UI
			console.warn("useAudioPeaks: decode failed", err);
			return null;
		})
		.finally(() => {
			inFlight.delete(key);
		});

	inFlight.set(key, task);
	return task;
}

export function useAudioPeaks(url: string | null, bars: number): number[] | null {
	const [peaks, setPeaks] = useState<number[] | null>(() => {
		if (!url) return null;
		return peaksCache.get(cacheKey(url, bars)) ?? null;
	});
	const generationRef = useRef(0);

	useEffect(() => {
		if (!url) {
			setPeaks(null);
			return;
		}
		const key = cacheKey(url, bars);
		const cached = peaksCache.get(key);
		if (cached) {
			setPeaks(cached);
			return;
		}

		const generation = ++generationRef.current;
		void loadPeaks(url, bars).then((normalized) => {
			if (generation !== generationRef.current) return;
			if (normalized) setPeaks(normalized);
		});

		return () => {
			generationRef.current += 1;
		};
	}, [url, bars]);

	return peaks;
}
